<template>
  <component
    :is="buttonComponent"
    :disabled="isAcceptClicked"
    data-testid="ai-proposal-accept-button"
    @click="clicked"
  >
    <div v-if="waiting" class="sf-spinner sf-spinner-primary tw-inline-block" />
    {{ text }}
  </component>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import { useAIProposalAcceptButton } from '@app/application/reconciliation_proposal/stores/ai-proposal-accept-button'
import {
  SaveChangesStatus,
  SaveChangesStatuses,
} from '@app/application/reconciliation/types'
import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'

interface Props {
  text: string
  url: string
  proposalsBlocked: boolean
  workflowId: string
}

const props = defineProps<Props>()
const saveChangesStatus = ref<SaveChangesStatus>(SaveChangesStatuses.INITIAL)

// eslint-disable-next-line no-undef
defineOptions({
  name: 'AIReviewAcceptButton',
})

const aiProposalAcceptButtonStore = useAIProposalAcceptButton()
const { isAcceptClicked } = storeToRefs(aiProposalAcceptButtonStore)

const waiting = computed(
  () =>
    isAcceptClicked.value &&
    saveChangesStatus.value !== SaveChangesStatuses.ERROR
)

function clicked() {
  aiProposalAcceptButtonStore.triggerProposalAcceptance()

  window.eventHub.$emit('saveChanges', {
    successCallback: () => {
      saveChangesStatus.value = SaveChangesStatuses.SUCCESS
      acceptProposal()
    },
    errorCallback: () => {
      saveChangesStatus.value = SaveChangesStatuses.ERROR
    },
  })
}

function acceptProposal() {
  const data = [
    { key: 'block_proposals', value: props.proposalsBlocked.toString() },
    { key: 'company_workflow_id', value: props.workflowId },
  ]
  const url = props.url

  try {
    SFetch.postFormRedirect({
      url,
      method: 'POST',
      authenticityToken: window.Silverfin.csrfToken(),
      data,
    })
  } catch (e) {
    SfLog.warn('Failed to confirm reconciliation proposal', {
      error: e,
    })
  }
}

const buttonComponent = computed(() =>
  waiting.value ? SfButtonInverted : SfButton
)
</script>
